import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { batch, useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import { BoxShadow } from '../../../shared/GlobalStyles';
import Button from '../../../shared/_TBComponents/Button';
import { dashboardTracker } from '../../../shared/utils/amplitudeHelper';
import { setGraphThresholds, setIsFilterShowing, setTableThreshold } from '../ducks/vbtDashboardActions';

const AddText = styled('span')`
  align-content: center;
  color: ${(props) => (props.enabled ? '#77A7FF' : '#BDBDBD')};
  cursor: ${(props) => (props.enabled ? 'pointer' : 'default')};
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 400;
`;

const Badge = styled('div')`
  background-color: #444444;
  border-radius: 100px;
  color: #FFFFFF;
  display: flex;
  font-family: 'Nunito Sans';
  font-size: 11px;
  font-weight: bold;
  justify-content: center;
  position: absolute;
  right: -7px;
  top: -7px;
  width: 15px;
`;

const Divider = styled('span')`
  align-content: center;  
  color: #9E9E9E;
  line-height: 36px;
  margin: 0px 5px;
`;

const ErrorText = styled('div')`
  color: #C0392B;
  font-family: 'Nunito Sans';
  font-size: 14px;
  margin: -5px 0px 5px;
`;

const Filter = styled('div')`
  display: flex;
  flex-direction: row;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: bold;
`;

const FilterBottom = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
`;

const FilterContainer = styled('div')`
  background-color: #FFFFFF;
  border-radius: 4px;
  box-shadow: ${BoxShadow};
  cursor: default;
  display: flex;
  flex-direction: column;
  max-height: 420px;
  padding: 16px;
  position: absolute;
  top: 45px;
  z-index: 5;

  @media only screen and (min-width: 647px) and (max-width: 935px) {
    right: 0px;
  }
`;

const FilterHeader = styled('div')`
  display: flex;
  font-family: 'Nunito Sans';
  font-size: 20px;
  font-weight: 700;
  justify-content: space-between;
  margin-bottom: 0px;
`;

const Max = styled('input')`
  border-radius: 4px;
  font-size: 16px;
  padding: 8px 16px;
  width: 125px;
`;

const Min = styled('input')`
  border-radius: 4px;
  font-size: 16px;
  padding: 8px 16px;
  width: 125px;
`;

const SubText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12px;
  font-weight: 100;
  margin-bottom: 10px;
`;

const ThresholdContainer = styled('div')`
  align-items: center;
  display: flex;
`;

const Wrapper = styled('div')`
  margin: 5px 0px;
  position: relative;
`;

const ThresholdFilter = ({ isMulti }) => {
  const graphThresholds = useSelector((state) => state.vbtDashboard.data.graphThresholds);
  const isFilterShowing = useSelector((state) => state.vbtDashboard.ui.isFilterShowing);
  const tableThreshold = useSelector((state) => state.vbtDashboard.data.tableThreshold);

  const [dupError, setDupError] = useState(false);
  const [errors, setErrors] = useState({ min: false, max: false });
  const [errorsMulti, setErrorsMulti] = useState(graphThresholds?.length
    ? graphThresholds.map(() => ({ min: false, max: false }))
    : [{ min: false, max: false }]);
  const [threshold, setThreshold] = useState({ min: tableThreshold.min ?? '', max: tableThreshold.max ?? '' });
  const [thresholds, setThresholds] = useState(graphThresholds?.length ? graphThresholds : [{ min: '', max: '' }]);

  const disableControl = errors.min || errors.max || threshold.min === '' || threshold.max === '';
  const disableControlMulti = thresholds.some((t, i) => {
    const err = errorsMulti[i] || {};
    return (
      err.min
    || err.max
    || t.min === ''
    || t.max === ''
    );
  });

  const badgeMulti = graphThresholds.length;
  const showBadge = Object.keys(tableThreshold).length > 0;
  const showClearBtn = threshold.min !== '' || threshold.max !== '';
  const showClearBtnMulti = thresholds.some((t) => t.min !== '' || t.max !== '');

  const dispatch = useDispatch();

  const handleAddThreshold = () => {
    if (thresholds.length >= 3) return;
    setThresholds([...thresholds, { min: '', max: '' }]);
    setErrors([...errorsMulti, { min: false, max: false }]);
  };

  const handleClear = () => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Analysis - Filter', 'Threshold filter cleared');
    dispatch(setTableThreshold({}));
    setErrors({ min: false, max: false });
    setThreshold({ min: '', max: '' });
  };

  const handleClearMulti = () => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Overview - Filter', 'Threshold filter cleared');
    setErrorsMulti([{ min: false, max: false }]);
    setThresholds([{ min: '', max: '' }]);
    setDupError(false);
    dispatch(setGraphThresholds([]));
  };

  const handleClose = () => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Analysis - Filter', 'Threshold filter closed');
    dispatch(setIsFilterShowing(false));
  };

  const handleFilter = () => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', `${isMulti ? 'Overview' : 'Analysis'} - Filter`, 'Threshold filter clicked');
    dispatch(setIsFilterShowing(!isFilterShowing));
  };

  const handleRemoveThreshold = (index) => {
    const updatedErrors = errorsMulti.filter((_, i) => i !== index);
    const updatedThresholds = thresholds.filter((_, i) => i !== index);
    setDupError(false);
    setErrorsMulti(updatedErrors);
    setThresholds(updatedThresholds);
  };

  const handleTableThresholdChange = (field, value) => {
    // Limit to 2 decimal places
    const formattedValue = value.includes('.')
      ? value.replace(/\.(\d{0,2}).*/, '.$1')
      : value;

    setThreshold((prevThreshold) => {
      const updated = { ...prevThreshold, [field]: formattedValue };
      const newMin = updated.min !== undefined ? updated.min : '';
      const newMax = updated.max !== undefined ? updated.max : '';
      const hasValue = newMin !== '' || newMax !== '';

      setErrors(() => ({
        min: hasValue && (newMin === '' || Number(newMin) <= 0),
        max: hasValue && (newMax === '' || Number(newMax) <= 0 || (newMin !== '' && Number(newMax) <= Number(newMin))),
      }));

      return updated;
    });
  };

  const handleThresholdChange = (index, field, value) => {
    // Limit to 2 decimal places
    const formattedValue = value.includes('.')
      ? value.replace(/\.(\d{0,2}).*/, '.$1')
      : value;

    setDupError(false);
    setThresholds((prevThresholds) => {
      const updatedThresholds = [...prevThresholds];
      const prev = updatedThresholds[index] || { min: '', max: '' };
      const updated = { ...prev, [field]: formattedValue };
      updatedThresholds[index] = updated;

      setErrorsMulti((prevErrors) => {
        const updatedErrors = [...prevErrors];
        const newMin = updated.min;
        const newMax = updated.max;
        const hasValue = newMin !== '' || newMax !== '';

        updatedErrors[index] = {
          min: hasValue && (newMin === '' || Number(newMin) <= 0),
          max: hasValue && (newMax === '' || Number(newMax) <= 0 || (newMin !== '' && Number(newMax) <= Number(newMin))),
        };

        return updatedErrors;
      });

      return updatedThresholds;
    });
  };

  // Validate thesholds and convert to numeric values before saving
  const isValidThreshold = (t) => {
    const min = Number(t.min);
    const max = Number(t.max);
    return (
      t.min !== '' && t.max !== ''
          && !Number.isNaN(min) && !Number.isNaN(max)
    );
  };

  const mapToNumeric = (t) => ({
    ...t,
    min: Number(t.min),
    max: Number(t.max),
  });

  const handleUpdate = () => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Analysis - Filter', 'Threshold filter updated');

    const validThreshold = isValidThreshold(threshold) ? mapToNumeric(threshold) : {};

    batch(() => {
      dispatch(setTableThreshold(validThreshold));
      dispatch(setIsFilterShowing(false));
    });
  };

  const handleUpdateMulti = () => {
    const validThresholds = thresholds.filter(isValidThreshold).map(mapToNumeric);
    // Normalize thresholds by converting to numbers and creating normalized strings
    const normalizedThresholds = validThresholds.map((t) => {
      const min = Number(t.min);
      const max = Number(t.max);
      return {
        min,
        max,
        normalizedString: `${min}-${max}`,
      };
    });
    // Check for duplicates using normalized values
    const thresholdStrings = normalizedThresholds.map((t) => t.normalizedString);
    const uniqueThresholdStrings = [...new Set(thresholdStrings)];
    const hasDuplicateThresholds = thresholdStrings.length !== uniqueThresholdStrings.length;

    if (hasDuplicateThresholds) {
      setDupError(true);
      return;
    }

    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Overview - Filter', 'Threshold filter updated');

    batch(() => {
      dispatch(setGraphThresholds(validThresholds));
      dispatch(setIsFilterShowing(false));
    });
  };

  const preventEAndMinus = (e) => {
    if (e.key === 'e' || e.key === 'E' || e.key === '-') {
      e.preventDefault();
    }
  };

  // Update thresholds if they get changed during dash save
  useEffect(() => {
    setThreshold({ min: tableThreshold.min ?? '', max: tableThreshold.max ?? '' });
    setThresholds(graphThresholds?.length ? graphThresholds : [{ min: '', max: '' }]);
  }, [graphThresholds, tableThreshold]);

  if (isMulti) {
    return (
      <Wrapper>
        <Button
          icon='filter'
          name='Filter'
          onClick={handleFilter}
          rounded
          styledWrapperProps={{ height: '100%' }}
          upperCase
        />
        {badgeMulti > 0 ? (
          <Badge>{badgeMulti}</Badge>
        ) : null}
        {isFilterShowing && (
        <FilterContainer>
          <FilterHeader>
            Thresholds
            <AddText
              enabled={thresholds.length < 3}
              onClick={handleAddThreshold}
            >
              + Add Threshold
            </AddText>
          </FilterHeader>
          <SubText>
            Visualize target by adding minimum and maximum thresholds below
          </SubText>
          {dupError ? (
            <ErrorText>
              You cannot have duplicate thresholds
            </ErrorText>
          ) : null}
          <Filter style={{ flexDirection: 'column', gap: '12px' }}>
            {thresholds.map((t, i) => (
              <ThresholdContainer key={`threshold_${i + 1}`}>
                <Min
                  id={`min_${i + 1}`}
                  min={0}
                  onChange={(e) => handleThresholdChange(i, 'min', e.target.value)}
                  onKeyDown={preventEAndMinus}
                  placeholder='0.4 m/s'
                  step='0.1'
                  style={{
                    border: errorsMulti[i]?.min ? '2px solid #FF0024' : '2px solid #E0E0E0',
                  }}
                  type='number'
                  value={t.min}
                />
                <Divider>-</Divider>
                <Max
                  id={`max_${i + 1}`}
                  min={0}
                  onChange={(e) => handleThresholdChange(i, 'max', e.target.value)}
                  onKeyDown={preventEAndMinus}
                  placeholder='0.6 m/s'
                  step='0.1'
                  style={{
                    border: errorsMulti[i]?.max ? '2px solid #FF0024' : '2px solid #E0E0E0',
                  }}
                  type='number'
                  value={t.max}
                />
                {thresholds.length > 1 && (
                <Button
                  icon='remove'
                  iconOnly
                  name=''
                  noBorder
                  onClick={() => handleRemoveThreshold(i)}
                  styledWrapperProps={{ marginLeft: '8px' }}
                />
                )}
              </ThresholdContainer>
            ))}
          </Filter>
          <FilterBottom>
            <Button
              bgColor='#444444'
              disabled={disableControlMulti || dupError}
              fullWidth
              name='Update'
              noHover={disableControlMulti || dupError}
              onClick={handleUpdateMulti}
              rounded
              txtColor='#FFFFFF'
              upperCase
            />
            {showClearBtnMulti ? (
              <Button
                fullWidth
                name='Clear'
                onClick={handleClearMulti}
                rounded
                styledWrapperProps={{ marginTop: '5px' }}
                upperCase
              />
            ) : null}
          </FilterBottom>
        </FilterContainer>
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Button
        icon='filter'
        name='Filter'
        onClick={handleFilter}
        rounded
        styledWrapperProps={{ height: '100%' }}
        upperCase
      />
      {showBadge ? (
        <Badge>1</Badge>
      ) : null}
      {isFilterShowing && (
      <FilterContainer>
        <FilterHeader>
          Threshold
          <Button
            icon='remove'
            iconOnly
            name=''
            noBorder
            onClick={handleClose}
          />
        </FilterHeader>
        <SubText>
          Visualize target by adding a minimum and maximum threshold below
        </SubText>
        <Filter>
          <Min
            id='min'
            min={0}
            onChange={(e) => handleTableThresholdChange('min', e.target.value)}
            onKeyDown={preventEAndMinus}
            placeholder='0.4 m/s'
            step='0.1'
            style={{
              border: errors.min ? '2px solid #FF0024' : '2px solid #E0E0E0',
            }}
            type='number'
            value={threshold.min}
          />
          <Divider>-</Divider>
          <Max
            id='max'
            min={0}
            onChange={(e) => handleTableThresholdChange('max', e.target.value)}
            onKeyDown={preventEAndMinus}
            placeholder='0.6 m/s'
            step='0.1'
            style={{
              border: errors.max ? '2px solid #FF0024' : '2px solid #E0E0E0',
            }}
            type='number'
            value={threshold.max}
          />
        </Filter>
        <FilterBottom>
          <Button
            bgColor='#444444'
            disabled={disableControl}
            fullWidth
            name='Update'
            noHover={disableControl}
            onClick={handleUpdate}
            rounded
            txtColor='#FFFFFF'
            upperCase
          />
          {showClearBtn ? (
            <Button
              fullWidth
              name='Clear'
              onClick={handleClear}
              rounded
              styledWrapperProps={{ marginTop: '5px' }}
              upperCase
            />
          ) : null}
        </FilterBottom>
      </FilterContainer>
      )}
    </Wrapper>
  );
};

ThresholdFilter.propTypes = {
  isMulti: PropTypes.bool,
};

ThresholdFilter.defaultProps = {
  isMulti: false,
};

export default ThresholdFilter;
